/** Helpers for the API's single error shape: `{ error: { code, message, details } }`. */

interface ApiErrorBody {
  error: { code: string; message: string };
}

function hasStatus(error: unknown): error is { status: number | string; data?: unknown } {
  return typeof error === 'object' && error !== null && 'status' in error;
}

function isApiErrorBody(data: unknown): data is ApiErrorBody {
  if (typeof data !== 'object' || data === null || !('error' in data)) return false;
  const body = (data as { error: unknown }).error;
  return typeof body === 'object' && body !== null && 'code' in body;
}

/** The machine-readable error code sent by the API, if this is an API error. */
export function errorCode(error: unknown): string | undefined {
  if (hasStatus(error) && isApiErrorBody(error.data)) return error.data.error.code;
  return undefined;
}

/** The `details` the API attached to an error, e.g. the list of problems in a draft. */
export function errorDetails(error: unknown): unknown {
  if (!hasStatus(error) || !isApiErrorBody(error.data)) return undefined;
  return (error.data.error as { details?: unknown }).details;
}

export function errorStatus(error: unknown): number | undefined {
  return hasStatus(error) && typeof error.status === 'number' ? error.status : undefined;
}

/**
 * True when the request may succeed if simply repeated later: the network is down,
 * the request timed out, or the server is temporarily failing.
 */
export function isTransientError(error: unknown): boolean {
  if (!hasStatus(error)) return false;
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') return true;
  return typeof error.status === 'number' && (error.status >= 500 || error.status === 429);
}
